import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { signIn, fetchAuthSession, signUp } from 'aws-amplify/auth';

import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';

import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import KeyboardCapslockIcon from '@mui/icons-material/KeyboardCapslock';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';

import PasswordStrengthBar from '../../components/PasswordStrengthBar';

import useFetch from '../../hooks/useFetch';
import useAsync from '../../hooks/useAsync';
import { getTenantFromUrl } from '../../utils/tenant';

import './EnterpriseSignUp.scss';

export default function EnterpriseSignUp({ userType }) {
  const [userData, setUserData] = useState({});
  const [userPassword, setUserPassword] = useState('');
  const [finalizeError, setFinalizeError] = useState('');

  const [showPassword, setShowPassword] = useState(false);
  const [passwordCapsLockIsOn, setPasswordCapsLockIsOn] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState('Weak');

  const [finalizingUser, setFinalizingUser] = useState(false);
  const [passwordCreated, setPasswordCreated] = useState(false);

  const [, updateEnterpriseUserRequest] = useFetch();

  const [, signUpRequest] = useAsync();
  const [, firstSignInRequest] = useAsync();
  const [, authSessionRequest] = useAsync();


  useEffect(() => {
    const urlParamsOnPgLoad = new URLSearchParams(window.location.search);
    const email = (urlParamsOnPgLoad.get('email') || '').trim().toLowerCase();
    const firstName = urlParamsOnPgLoad.get('firstName');
    const lastName = urlParamsOnPgLoad.get('lastName');
    const phoneNumber = urlParamsOnPgLoad.get('phoneNumber');
    const companyId = urlParamsOnPgLoad.get('companyId');
    const investorCompanyId = urlParamsOnPgLoad.get('investorCompanyId') || companyId;
    const enterpriseCompanyId = urlParamsOnPgLoad.get('enterpriseCompanyId') || companyId;
    setUserData({
      email,
      firstName,
      lastName,
      phoneNumber,
      companyId,
      investorCompanyId,
      enterpriseCompanyId,
    });
  }, []);

  function isUsernameExistsError(error) {
    const message = `${error?.name || ''} ${error?.message || ''} ${error?.toString() || ''}`.toLowerCase();
    return message.includes('usernameexistsexception') || message.includes('user already exists');
  }

  function completeVerifiedAccount(normalizedEmail) {
    authSessionRequest({
      promise: () => fetchAuthSession(),
      onSuccess: (authSession) => {
        const tokenPayload = authSession.tokens.idToken.payload || {};
        const accessPayload = authSession.tokens.accessToken?.payload || {};
        const { sub } = tokenPayload;
        const cognitoUsername = tokenPayload['cognito:username']
          || accessPayload.username
          || sub;
        const isRedwoodTenant = getTenantFromUrl() === 'redwoodseed';
        const resolvedCompanyId = userData.enterpriseCompanyId
          || userData.companyId
          || tokenPayload['custom:companyId'];
        const endpoint = `/accounts/update-verified-${userType}-account`;
        const accountData = userType === 'investor'
          ? {
            accountId: sub,
            cognitoUsername,
            companyId: userData.investorCompanyId || resolvedCompanyId,
            email: normalizedEmail,
            redwoodseedUser: isRedwoodTenant,
          }
          : {
            enterpriseCompanyId: resolvedCompanyId,
            firstName: userData.firstName,
            lastName: userData.lastName,
            phoneNumber: userData.phoneNumber,
            accountId: sub,
            cognitoUsername,
            email: normalizedEmail,
            companyId: resolvedCompanyId,
            redwoodseedUser: isRedwoodTenant,
          };
        if (!accountData.companyId) {
          setFinalizingUser(false);
          setFinalizeError('Missing company id for this invitation. Please contact support.');
          return;
        }
        const redirectHostname = isRedwoodTenant
          ? process.env.REACT_APP_ENTERPRISE_HOSTNAME
          : process.env.REACT_APP_INITIO_ENTERPRISE_HOSTNAME;
        updateEnterpriseUserRequest({
          url: endpoint,
          body: accountData,
          enterpriseCall: true,
          onSuccess: () => {
            fetchAuthSession({ forceRefresh: true }).finally(() => {
              setPasswordCreated(true);
              setFinalizingUser(false);
              setTimeout(() => {
                window.location.assign(
                  `${redirectHostname}${userType === 'enterprise' ? '/first-enterprise-sign-in' : ''}`
                );
              }, 2000);
            });
          },
          onError: (error) => {
            setFinalizingUser(false);
            setFinalizeError(error || 'We could not verify this invitation. Please confirm your invite details.');
          },
        });
      },
      onError: () => {
        setFinalizingUser(false);
        setFinalizeError('Unable to fetch session details. Please try again.');
      },
    });
  }

  function signInAndComplete(normalizedEmail) {
    firstSignInRequest({
      promise: () => signIn({ username: normalizedEmail, password: userPassword }),
      onSuccess: () => completeVerifiedAccount(normalizedEmail),
      onError: () => {
        setFinalizingUser(false);
        setFinalizeError('Could not sign in after password creation. Please try again.');
      },
    });
  }

  async function finalizeEnterpriseUser(event) {
    event.preventDefault();
    setFinalizeError('');
    const normalizedEmail = (userData.email || '').trim().toLowerCase();
    const companyId = userType === 'investor' ? userData.investorCompanyId : userData.enterpriseCompanyId;
    if (!companyId) {
      setFinalizeError('Missing company id for this invitation. Please contact support.');
      return;
    }
    const isRedwoodTenant = getTenantFromUrl() === 'redwoodseed';
    setFinalizingUser(true);
    signUpRequest({
      promise: () => signUp({
        username: normalizedEmail, password: userPassword,
        options: {
          userAttributes: {
            email: normalizedEmail,
            'custom:firstName': userData.firstName,
            'custom:lastName': userData.lastName,
            'custom:phoneNumber': userData.phoneNumber,
            'custom:userType': userType,
            'custom:companyId': companyId,
            'custom:tenant': isRedwoodTenant ? 'redwood' : 'initio',
            'custom:firstTimeExp': userType === 'enterprise' ? '1' : '0',
          },
        },
      }),
      onSuccess: () => signInAndComplete(normalizedEmail),
      onError: (error) => {
        if (isUsernameExistsError(error)) {
          signInAndComplete(normalizedEmail);
          return;
        }
        setFinalizingUser(false);
        setFinalizeError('Password setup failed. Please verify invitation details and retry.');
      },
    });
  }

  if (passwordCreated) {
    return (
      <main className="EnterpriseSignUp">
        <h4>Password created</h4>
        <div className="success-header">
          <div className="check-icon">
            <CheckRoundedIcon />
          </div>
          <p>Your password was created successfully. You will be redirected to the enterprise app shortly.</p>
        </div>
      </main>
    );
  }

  return (
    <main className="EnterpriseSignUp">
      <h4>Create password to continue</h4>
      <div className="success-header">
        <div className="check-icon">
          <CheckRoundedIcon />
        </div>
        <p>Great, let&apos;s get started! Simply create a password to login.</p>
      </div>
      <form onSubmit={(e) => finalizeEnterpriseUser(e)}>
        <TextField
          label="Email"
          className="email-field"
          value={userData.email || ''}
          InputLabelProps={{ shrink: true }}
          disabled
        />
        <TextField
          label="Password"
          className="password-field"
          type={showPassword ? 'text' : 'password'}
          value={userPassword || ''}
          onChange={(e) => setUserPassword(e.target.value)}
          onKeyDown={(e) => setPasswordCapsLockIsOn(e.getModifierState('CapsLock'))}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {passwordCapsLockIsOn && <KeyboardCapslockIcon />}
                <IconButton onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <VisibilityOutlinedIcon /> : <VisibilityOffOutlinedIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <PasswordStrengthBar
          passwordInput={userPassword}
          passwordStrength={passwordStrength}
          setPasswordStrength={setPasswordStrength}
        />
        {finalizeError && <p className="signup-error">{finalizeError}</p>}
        <Button
          type="submit"
          className={`confirm-login-btn ${finalizingUser ? 'loading' : ''}`}
          disabled={passwordStrength === 'Weak' || finalizingUser}
        >
          {finalizingUser ? (
            <>
              <span className="dots-circle-spinner" />
              Creating...
            </>
          ) : 'Create Password'}
        </Button>
      </form>
    </main>
  );
}

EnterpriseSignUp.propTypes = { userType: PropTypes.string.isRequired };
